import { useCallback, useEffect, useState } from 'react';
import ApiDatabase from 'server';

// The routes' own keys, so that closing the panel cancels what it asked for
// and nothing else (the run follow-up has its own, `invoiceDetail|run`).
const DETAIL_ABORT_KEY = 'admin/invoices/:number';
const PDF_ABORT_KEY = 'admin/invoices/:number/pdf';

/**
 * The invoice of the detail panel (JBWEB-1274), read by its BeSTT number.
 * `failure` is "notFound" or "failed" when it could not be read. A reload
 * keeps the invoice on screen until the new one comes: the panel is never
 * emptied at the end of an import.
 */
export const useInvoiceDetail = (number) => {
  const [state, setState] = useState({
    invoice: null,
    isLoading: true,
    failure: null,
  });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!number) return undefined;
    let active = true;
    setState((previous) => ({ ...previous, isLoading: true, failure: null }));
    ApiDatabase.getInvoiceDetail(
      { number },
      (data) => {
        if (!active) return;
        const invoice = data?.invoice ?? null;
        setState({
          invoice,
          isLoading: false,
          failure: invoice ? null : 'failed',
        });
      },
      (error) => {
        if (!active || error?.name === 'CanceledError') return;
        // Never the error object in the console: axios keeps the request, token included.
        setState((previous) => ({
          ...previous,
          isLoading: false,
          failure: error?.response?.status === 404 ? 'notFound' : 'failed',
        }));
      },
      { abortKey: DETAIL_ABORT_KEY },
    );
    return () => {
      active = false;
    };
  }, [number, reloadKey]);

  useEffect(() => () => ApiDatabase.abortRequest(DETAIL_ABORT_KEY), []);

  const reload = useCallback(() => setReloadKey((key) => key + 1), []);
  // The invoice an edit answered with, read back by the api, so that the
  // panel does not read it again.
  const replace = useCallback(
    (invoice) => setState({ invoice, isLoading: false, failure: null }),
    [],
  );
  return { ...state, reload, replace };
};

// What the preview says for each refusal of the PDF route.
const pdfFailureOf = (error) => {
  const status = error?.response?.status;
  const code = error?.response?.data?.errorCode;
  if (status === 503 || code === 'INVOICE_PREVIEW_BUSY') return 'busy';
  if (status === 404) return 'notFound';
  if (status === 502) return 'besttFailed';
  return 'failed';
};

/**
 * The PDF of the invoice, as a Blob (JBWEB-1274). `failure` is "busy" (an
 * import is running: BeSTT is not asked), "notFound", "besttFailed" or
 * "failed".
 */
export const useInvoicePdf = (number) => {
  const [state, setState] = useState({
    blob: null,
    isLoading: true,
    failure: null,
  });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!number) return undefined;
    let active = true;
    setState({ blob: null, isLoading: true, failure: null });
    ApiDatabase.getInvoicePdf(
      { number },
      (blob) => {
        if (!active) return;
        setState({ blob, isLoading: false, failure: null });
      },
      (error) => {
        if (!active || error?.name === 'CanceledError') return;
        setState({
          blob: null,
          isLoading: false,
          failure: pdfFailureOf(error),
        });
      },
      { abortKey: PDF_ABORT_KEY },
    );
    return () => {
      active = false;
    };
  }, [number, reloadKey]);

  useEffect(() => () => ApiDatabase.abortRequest(PDF_ABORT_KEY), []);

  const reload = useCallback(() => setReloadKey((key) => key + 1), []);
  // A PDF read but that react-pdf cannot draw.
  const markUnreadable = useCallback(
    () => setState({ blob: null, isLoading: false, failure: 'failed' }),
    [],
  );
  return { ...state, reload, markUnreadable };
};
